import AreaGraph from "./AreaGraph";
import React, { useEffect, useState } from "react";
import {
  formatBodyMeasure,
  getBodyMeasures,
  mapBodyMeasureChart,
  mapMeasures,
  processBodyMeasurements,
  standardMeasures,
} from "../body-composition.helper";
import SvgIcon from "../../../../../parts/SvgIcon";

const BodyMeasureStats = ({
  data,
  athlete,
  date,
  units,
  organizationCoachParam,
}) => {
  const [selectedPart, setSelectedPart] = useState("neck");
  const [gain, setGain] = useState(0);
  const [sumMeasurements, setSumMeasurements] = useState(null);
  const unitLabel = units === "metric" ? "cm" : "inch";

  useEffect(() => {
    const sumData = processBodyMeasurements(data, units);

    setSumMeasurements({ ...sumData });
  }, [data]);

  useEffect(() => {
    if (sumMeasurements) {
      setSelectedPart("neck");
      setGain(sumMeasurements.formattedChanges["neck"] || 0);
    }
  }, [sumMeasurements]);

  const formattedMeasures = getBodyMeasures(data, units);
  //make an array with the keys of the object, part: key, measure: value, gain: 1.5
  const formattedData = Object.keys(formattedMeasures).map((key) =>
    mapMeasures(key, formattedMeasures)
  );

  const onPartClick = (part, gain) => {
    setSelectedPart(part);
    setGain(gain);
  };

  //filter bodyMeasurements by selectedPart and target the bodyMeasurements key matching selected part
  const filteredData = data.length
    ? data.filter(
        ({ bodyMeasurements }) =>
          bodyMeasurements && bodyMeasurements[selectedPart]
      )
    : [];

  const areaGraphData = filteredData.map((data) =>
    mapBodyMeasureChart(data, selectedPart, units)
  );

  // Run standard measuers and for each key, check if the key is in formattedData.part and if that doesnt exist then add
  // it to the array as { part: key, measure: 0, gain: 0}
  const standardMeasuresArray = Object.keys(standardMeasures).map((key) => {
    const isPart = formattedData.find((part) => part.part === key);

    // If there is no body part, add 0 to that part
    if (formattedMeasures[key] === undefined) formattedMeasures[key] = 0;

    if (!isPart) {
      return { part: key, measure: 0, gain: 0 };
    }

    return isPart;
  });

  return (
    <article id="measurements">
      <Header athlete={athlete} date={date} />
      <section>
        <LeftZone
          selectedPart={selectedPart}
          cleanData={areaGraphData}
          gain={gain}
          unit={unitLabel}
        />
        {sumMeasurements && (
          <Table
            data={standardMeasuresArray}
            onClick={onPartClick}
            sumData={sumMeasurements}
            selectedPart={selectedPart}
            unit={unitLabel}
          />
        )}
      </section>
      <footer className="only-show-mobile">
        <a
          className="composition-see-more"
          href={`
            /athlete-portal/metrics/body-measurements?date=${date}&athlete=${athlete}&bodycomp=true
            `}
        >
          <span>See more</span>
          <SvgIcon
            src="/img/new-icons/arrow-square-right-gradient.svg"
            noFilterOnLight={true}
          />
        </a>
      </footer>
    </article>
  );
};

const LeftZone = ({ selectedPart, cleanData, gain, unit }) => (
  <div>
    {selectedPart && (
      <div>
        {gain !== 0 && (
          <div>
            <span>{formatBodyMeasure(selectedPart)}</span>
            {getGainOrLoss(gain, unit)}
          </div>
        )}
        <div>
          <AreaGraph
            data={cleanData}
            targetValue={"value"}
            label={"Body Measure"}
            description={"Measure"}
            units={unit}
            graphId={"body-measure-graph"}
          />
        </div>
      </div>
    )}
  </div>
);

const Header = ({ date, athlete }) => (
  <header>
    <div>
      <h4> Body Measurements</h4>
      <p className="p4">(Last 3 months)</p>
    </div>
    <a
      className="composition-see-more mobile-hidden"
      href={`
        /athlete-portal/metrics/body-measurements?date=${date}&athlete=${athlete}&bodycomp=true
        `}
    >
      <span>See more</span>
      <SvgIcon
        src="/img/new-icons/arrow-square-right-gradient.svg"
        noFilterOnLight={true}
      />
    </a>
  </header>
);

//date is undefined here, so we need to fix it
const getGainOrLoss = (value, unit, table) => {
  const negativeValue = value < 0;
  //format the value with - or +  if it loss or gain
  const formattedValue = negativeValue ? `${value}` : `+${value}`;
  if (table)
    return (
      <td>
        <span>
          <picture>
            <img
              src={`/img/new-icons/${
                !negativeValue ? "arrow-up-blue" : "green-arrow-down-rounded"
              }.svg`}
            />
          </picture>
          <span>{formattedValue + unit}</span>
        </span>
      </td>
    );
  return (
    <span className={!negativeValue ? "text-[--danger]" : "text-[--success]"}>
      <picture>
        <img
          src={`/img/new-icons/${
            !negativeValue ? "arrow-up-blue" : "green-arrow-down-rounded"
          }.svg`}
        />
      </picture>
      <span>
        {" "}
        {formattedValue} {unit}
      </span>
    </span>
  );
};

const Table = ({ data, onClick, sumData, selectedPart, unit }) => (
  <section role="table-wrapper">
    <table role="report-table">
      <tbody role="table-body">
        {
          //data is standard measure object { neck: { measure, gain , part}
          data.map((bodyMeasurement, idx) => (
            <Item
              key={idx}
              part={bodyMeasurement.part}
              measure={bodyMeasurement.measure}
              gain={sumData.formattedChanges[bodyMeasurement.part] || 0}
              onClick={() =>
                onClick(
                  bodyMeasurement.part,
                  sumData.formattedChanges[bodyMeasurement.part] || 0
                )
              }
              selected={bodyMeasurement.part === selectedPart}
              unit={unit}
            />
          ))
        }
      </tbody>
    </table>
  </section>
);

const Item = ({ part, measure, gain, onClick, unit, selected = false }) => (
  <tr className={selected ? " bg-active" : ""} onClick={onClick}>
    <td>{formatBodyMeasure(part)}</td>
    <td>
      {measure} {unit}
    </td>
    {getGainOrLoss(gain, unit, true)}
  </tr>
);

export default BodyMeasureStats;
